// src/pages/Organizations/OrganizationDetailPage.js
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, Box, Grid, Avatar, Button, Chip,
  Divider, Tabs, Tab, Card, CardContent, CardMedia, List, ListItem,
  ListItemIcon, ListItemText, CircularProgress, Alert, Breadcrumbs,
  Link, Rating, Stack
} from '@mui/material';
import {
  ArrowBack, LocationOn, Email, Phone, Language, Verified,
  People, Lightbulb, Build, LinkedIn, Twitter
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../../services/api';

// Helper component for cleaner Tab management
const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const OrganizationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [problems, setProblems] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const orgData = await api.get(`/organizations/${id}/`);
        setOrganization(orgData);

        // Fetch related data concurrently
        const [problemsRes, solutionsRes, membersRes] = await Promise.all([
          api.get(`/problems/?organization=${id}`).catch(() => ({ results: [] })),
          api.get(`/solutions/?provider=${id}`).catch(() => ({ results: [] })),
          api.get(`/organizations/${id}/members/`).catch(() => []),
        ]);

        setProblems(problemsRes.results || []);
        setSolutions(solutionsRes.results || []);
        setMembers(membersRes || []);
      } catch (err) {
        setError(err.data?.message || 'Failed to load organization details');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [id]);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !organization) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Organization not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/organizations')} sx={{ mt: 2 }}>
          Back to Directory
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Navigation */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/" color="inherit" underline="hover">Home</Link>
        <Link component={RouterLink} to="/organizations" color="inherit" underline="hover">Organizations</Link>
        <Typography color="text.primary">{organization.name}</Typography>
      </Breadcrumbs>

      <Button startIcon={<ArrowBack />} onClick={() => navigate('/organizations')} sx={{ mb: 3 }}>
        Back
      </Button>

      {/* Hero Section */}
      <Paper sx={{ p: { xs: 3, md: 4 }, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }} sx={{ mb: 3 }}>
              <Avatar
                src={organization.logo}
                variant="rounded"
                sx={{ width: 120, height: 120, boxShadow: 1 }}
              >
                {organization.name[0]}
              </Avatar>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h3" fontWeight="bold">{organization.name}</Typography>
                  {organization.is_verified && <Verified color="primary" sx={{ fontSize: 32 }} />}
                </Stack>
                <Typography variant="h6" color="text.secondary">
                  {organization.organization_type} • {organization.size}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2">{organization.city}, {organization.country}</Typography>
                  {organization.industry && (
                    <Chip label={organization.industry.name} size="small" color="primary" variant="outlined" sx={{ ml: 2 }} />
                  )}
                </Stack>
              </Box>
            </Stack>

            <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 3 }}>
              {organization.description || 'This organization hasn\'t provided a description yet.'}
            </Typography>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {organization.categories?.map((cat) => (
                <Chip key={cat.id} label={cat.name} variant="soft" color="info" />
              ))}
            </Stack>
          </Grid>

          {/* Sidebar Contact Info */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'grey.50' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Contact & Social</Typography>
                <List dense disablePadding>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}><Email fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Email" secondary={organization.email} />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}><Phone fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Phone" secondary={organization.phone || 'N/A'} />
                  </ListItem>
                  {organization.website && (
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}><Language fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary="Website" 
                        secondary={<Link href={organization.website} target="_blank" rel="noopener">{organization.website}</Link>} 
                      />
                    </ListItem>
                  )}
                </List>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Headquarters</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {organization.address_line1}{'\n'}
                  {organization.address_line2 && `${organization.address_line2}\n`}
                  {`${organization.city}, ${organization.state} ${organization.postal_code}`}{'\n'}
                  {organization.country}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  {organization.linkedin_url && (
                    <Button size="small" variant="outlined" startIcon={<LinkedIn />} href={organization.linkedin_url} target="_blank">LinkedIn</Button>
                  )}
                  {organization.twitter_url && (
                    <Button size="small" variant="outlined" startIcon={<Twitter />} href={organization.twitter_url} target="_blank">Twitter</Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs Content */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          indicatorColor="primary" 
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Overview" />
          <Tab label={`Problems (${problems.length})`} />
          <Tab label={`Solutions (${solutions.length})`} />
          <Tab label={`Team (${members.length})`} />
        </Tabs>

        {/* Tab 0: Overview */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Our Mission</Typography>
              <Typography paragraph color="text.secondary">
                {organization.description || 'No additional details available.'}
              </Typography>
              
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>Key Metrics</Typography>
              <Grid container spacing={2}>
                {[
                  { icon: <People />, label: 'Team', value: members.length },
                  { icon: <Lightbulb />, label: 'Problems', value: problems.length },
                  { icon: <Build />, label: 'Solutions', value: solutions.length },
                  { icon: <Verified />, label: 'Status', value: organization.is_verified ? 'Verified' : 'Pending' }
                ].map((stat, i) => (
                  <Grid item xs={6} key={i}>
                    <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                      {React.cloneElement(stat.icon, { color: 'primary', sx: { fontSize: 32 } })}
                      <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>{stat.value}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{stat.label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Recent Activity</Typography>
              {problems.length > 0 ? (
                problems.slice(0, 3).map((problem) => (
                  <Card key={problem.id} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="subtitle2" noWrap>{problem.title}</Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(problem.created_at).toLocaleDateString()}
                        </Typography>
                        <Chip label={problem.status} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">No recent activities to show.</Typography>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 1: Problems */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            {problems.length === 0 ? (
              <Box sx={{ py: 4, width: '100%', textAlign: 'center' }}>
                <Typography color="text.secondary">This organization hasn't posted any problems yet.</Typography>
              </Box>
            ) : (
              problems.map((problem) => (
                <Grid item xs={12} key={problem.id}>
                  <Card 
                    variant="outlined" 
                    sx={{ cursor: 'pointer', transition: '0.2s', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
                    onClick={() => navigate(`/problems/${problem.id}`)}
                  >
                    <CardContent>
                      <Typography variant="h6" color="primary">{problem.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {problem.description}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Chip label={problem.priority} size="small" color={problem.priority === 'critical' ? 'error' : 'default'} />
                        <Chip label={problem.status} size="small" variant="outlined" />
                        <Typography variant="caption" sx={{ alignSelf: 'center', ml: 'auto' }}>{problem.views_count} views</Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </TabPanel>

        {/* Tab 2: Solutions */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            {solutions.length === 0 ? (
              <Box sx={{ py: 4, width: '100%', textAlign: 'center' }}>
                <Typography color="text.secondary">No solutions provided by this organization.</Typography>
              </Box>
            ) : (
              solutions.map((solution) => (
                <Grid item xs={12} sm={6} key={solution.id}>
                  <Card 
                    variant="outlined"
                    sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                    onClick={() => navigate(`/solutions/${solution.id}`)}
                  >
                    <CardMedia
                      component="img"
                      height="160"
                      image={solution.cover_image || 'https://via.placeholder.com/400x200?text=Solution'}
                      alt={solution.title}
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom>{solution.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: 40, overflow: 'hidden' }}>
                        {solution.tagline || solution.description}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Rating value={solution.rating_avg} readOnly size="small" />
                        <Typography variant="caption">({solution.review_count})</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Chip label={solution.solution_type} size="small" variant="outlined" />
                        <Chip label={`${solution.subscriber_count} subs`} size="small" variant="soft" />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </TabPanel>

        {/* Tab 3: Team */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={2}>
            {members.length === 0 ? (
              <Box sx={{ py: 4, width: '100%', textAlign: 'center' }}>
                <Typography color="text.secondary">Team list is private or empty.</Typography>
              </Box>
            ) : (
              members.map((member) => (
                <Grid item xs={12} sm={6} md={4} key={member.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.light' }}>
                          {member.user_full_name?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">{member.user_full_name}</Typography>
                          <Typography variant="body2" color="primary">{member.role}</Typography>
                          {member.job_title && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {member.job_title}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      {member.department && (
                        <Chip label={member.department} size="small" sx={{ mt: 2, bgcolor: 'grey.100' }} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default OrganizationDetailPage;